import React, { useEffect, useRef } from 'react'
import { IEcommerceAnswerSchema, EcommerceCartProps } from '@aytm/app/swidget/base/questions/Ecommerce/types.ts'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '../../../modules/locale/useSurveyLocale.ts'
import useTimeline from '../../../modules/timeline/useTimeline.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import SpinButton from './components/SpinButton.tsx'
import Checkbox from './components/Checkbox.tsx'
import CartPriceColumn from './CartPriceColumn.tsx'
import style from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/amazon.module.scss'
import commonStyle from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/common.module.scss'
import { unescapeTags } from '@aytm/lib/common/func.js'
import { storeExtension } from '../constants.ts'
import ecoLeafIcon from './img/ecoLeaf.png'
import AssetsGate from '@aytm/app/swidget/base/questions/Ecommerce/components/AssetsGate.tsx'
import Spinner from './components/Spinner.tsx'

const getDate = (schema: IEcommerceAnswerSchema) => {
  const date = new Date()
  if (schema.prime) date.setDate(date.getDate() + schema.prime - 1)
  else {
    date.setDate(date.getDate() + ((schema.text?.length || 0) % 2) + 1)
    date.setDate(date.getDate() + ((schema.description?.length || 0) % 3) + 1)
    return date
  }
  return date
}

const Cart = ({ response, schema, totals, onUpdateResponse, closeCart, checkout, scrollTop }: EcommerceCartProps) => {
  const { t, languageTag, localeID } = useSurveyLocale()
  const viewport = useViewport()
  const na = schema.answers.find(answer => answer.na)
  const cart = response.answers.filter(r => r.v > 0 && r.id !== na?.id)
  const timeline = useTimeline()
  const cartRef = useRef<HTMLDivElement>(null)
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)

  const EXT = storeExtension('amazonw', localeID)
  const STORE = `${t('questions', 'ec', 'amazon', 'store')}.${EXT}`

  useEffect(() => {
    if (cartRef.current) {
      cartRef.current.focus()
    }
  }, [cart.length])

  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  const onCartSelect = (i: number) => {
    closeCart()
    response.selected = response.answers[i].id
    timeline.log({
      target: '0,0',
      type: 'open_popup',
      ref: response.answers[i].id
    })
    onUpdateResponse(response)
  }

  const onNaSelect = () => {
    timeline.log({
      type: 'na_select',
      ref: na.id
    })
    // Check if NA exists in response.answers, if not - add it
    response.answers.push({ id: na.id, v: 1 })
    onUpdateResponse(response)
  }

  const adjustQuantity = (qnt: -1 | 1, index: string, subscribe?: number) => {
    const answer = response.answers.find(answer => answer.id === index && answer.subscribe === subscribe)
    if (answer) {
      answer.v += qnt
    }
    onUpdateResponse(response)
    timeline.log({
      type: 'edit_in_cart',
      ref: answer.id,
      target: answer.v.toString()
    })
    if (answer?.subscribe) {
      timeline.log({
        type: 'subscribe_edit_in_cart',
        ref: answer.id,
        target: answer.v.toString(),
        context: { qty: answer.v, weeks: answer.subscribe, price: answer.price }
      })
    }
  }

  const onKeyDownAdjustQuantity = (event: React.KeyboardEvent, id: string, subscribe?: number) => {
    event.preventDefault()
    const answer = response.answers.find(answer => answer.id === id && answer.subscribe === subscribe)
    if (!answer) return

    switch (event.key) {
      case 'ArrowDown':
        answer.v -= 1
        break
      case 'ArrowUp':
        answer.v += 1
        break
    }
    onUpdateResponse(response)
    timeline.log({
      type: 'edit_in_cart',
      ref: id,
      target: answer.v.toString()
    })
    if (answer.subscribe) {
      timeline.log({
        type: 'subscribe_edit_in_cart',
        ref: id,
        target: answer.v.toString(),
        context: { qty: answer.v, weeks: answer.subscribe, price: answer.price }
      })
    }
  }

  const renderGreenBox = () => (
    <div className={style.greenBox}>
      <i className={`${totals[1] >= 35 ? style.green : style.blue}`} />

      <div>
        {totals[1] >= 35 ? (
          <>
            <span className={style.green}>{t('questions', 'ec', 'amazon', 'freeShipping1')}</span>&nbsp;{' '}
            {t('questions', 'ec', 'amazon', 'freeShipping2')}
          </>
        ) : (
          <>
            {t('questions', 'ec', 'amazon', 'add1')}{' '}
            <span
              className={style.red}
              dangerouslySetInnerHTML={{
                __html: formatPriceWithLocale(35 - totals[1], schema.price_mask, languageTag, true)
              }}
            />{' '}
            {t('questions', 'ec', 'amazon', 'add2')}
          </>
        )}
        &nbsp;
        <EmptyLink>
          <span className={style.link}>{t('questions', 'ec', 'amazon', 'seeDetails')}</span>
        </EmptyLink>
        .
      </div>
    </div>
  )

  const renderError = () => {
    if (!valid) {
      return (
        <div
          className={style.error}
          dangerouslySetInnerHTML={{
            __html:
              (schema.max_money_spent && totals[1] > schema.max_money_spent
                ? t<(max: string) => string>(
                    'questions',
                    'ec',
                    'maxSpentError'
                  )(formatPriceWithLocale(schema.max_money_spent, schema.price_mask, languageTag))
                : '') +
              String(
                schema.max_cart_size && totals[0] > schema.max_cart_size
                  ? t<(max: number) => string>('questions', 'ec', 'maxCartCountError')(schema.max_cart_size)
                  : ''
              )
          }}
        />
      )
    }
    return null
  }

  return (
    <div className={style.cartWrapper}>
      <button type="button" className={style.backToResults} onClick={closeCart}>
        <span>‹</span>&nbsp;&nbsp;&nbsp;{t('questions', 'ec', 'amazon', 'back')}
      </button>
      <div className={style.cart} ref={cartRef} tabIndex={0} aria-label={t('questions', 'ec', 'amazon', 'cartLabel')}>
        <div className={style.leftColumn}>
          <div className={style.activeCart}>
            {viewport.mobile && Boolean(cart.length) && (
              <EmptyLink>
                <div className={style.giftContainer}>
                  <Checkbox />
                  {t('questions', 'ec', 'amazon', 'sendGift')}
                </div>
              </EmptyLink>
            )}
            <div className={style.header}>
              {viewport.mobile
                ? !cart.length && t('questions', 'ec', 'amazon', 'emptyCart')
                : cart.length
                  ? t('questions', 'ec', 'amazon', 'shoppingCart')
                  : t('questions', 'ec', 'amazon', 'emptyCart')}
              <span className={style.priceHeader}>{t('questions', 'ec', 'amazon', 'price')}</span>
            </div>
            <div className={style.divider} />
            <div className={style.products}>
              {response.answers.map((r, ri) => {
                const answer = schema.answers.find(answer => answer.id === r.id)
                if (!answer) return null
                return (
                  <div key={ri}>
                    {r.v > 0 && r.id !== na?.id ? (
                      <div>
                        <div
                          className={style.cartProduct}
                          tabIndex={0}
                          aria-label={t('questions', 'ec', 'amazon', 'productLabel')}
                        >
                          {/* Each line waits for its own photo; an empty slot has nothing to wait for. */}
                          <AssetsGate
                            urls={[answer.image?.original_url && getMediumAssetPath(answer.image, 'transparent')]}
                            loader={<Spinner className={style.img} />}
                          >
                            <div
                              className={`${style.img} ${
                                answer.image && answer.image.original_url ? '' : commonStyle.emptyImage
                              }`}
                              {...(answer.image && answer.image.original_url
                                ? {
                                    role: 'img',
                                    'aria-label': answer.image?.alt_text || t('questions', 'img', 'noAlt')
                                  }
                                : {})}
                              onClick={() => onCartSelect(ri)}
                              style={
                                answer.image && answer.image.original_url
                                  ? {
                                      backgroundImage: `url(${getMediumAssetPath(answer.image, 'transparent')})`
                                    }
                                  : {}
                              }
                            ></div>
                          </AssetsGate>
                          <div className={style.textBlock}>
                            <div
                              className={style.text}
                              onClick={() => onCartSelect(ri)}
                              dangerouslySetInnerHTML={{ __html: answer.text }}
                            ></div>
                            <span className={style.inStock}>{t('questions', 'ec', 'amazon', 'inStock')}</span>

                            {answer.eco_label ? (
                              <EmptyLink>
                                <span
                                  className={`${commonStyle.ecoLabel} ${viewport.mobile ? commonStyle.ecoLabelMobile : ''}`}
                                >
                                  <img src={ecoLeafIcon} width={18} height={18} alt="" />
                                  {viewport.mobile ? (
                                    <span dangerouslySetInnerHTML={{ __html: answer.eco_label }} />
                                  ) : (
                                    <span className={commonStyle.arrowDown}>
                                      <span dangerouslySetInnerHTML={{ __html: answer.eco_label }} /> <i />
                                    </span>
                                  )}
                                </span>
                              </EmptyLink>
                            ) : (
                              ''
                            )}
                            <span className={style.base}>
                              {t('questions', 'ec', 'amazon', 'freeDelivery')}{' '}
                              <b>
                                {getDate(answer).toLocaleDateString(languageTag, {
                                  weekday: 'short',
                                  day: 'numeric',
                                  month: 'short'
                                })}
                              </b>{' '}
                              {t('questions', 'ec', 'amazon', 'availableAtCheckout')}
                            </span>
                            <span className={`${style.arrowDown} ${style.link}`}>
                              <EmptyLink>
                                <span>{t('questions', 'ec', 'amazon', 'freeReturns')}</span>
                                {/* <i /> */}
                              </EmptyLink>
                            </span>
                            <span className={style.gift}>
                              <EmptyLink>
                                <div className={style.checkbox}>
                                  <i></i>
                                </div>
                              </EmptyLink>
                              {t('questions', 'ec', 'amazon', 'isGift')}{' '}
                              <EmptyLink>
                                <span className={style.link}>{t('questions', 'ec', 'amazon', 'learnMore')}</span>
                              </EmptyLink>
                            </span>
                            {viewport.mobile && (
                              <>
                                <div className={style.priceBlockMoble}>
                                  {answer.bought && (
                                    <div className={style.bought}>
                                      <b>{unescapeTags(answer.bought)}</b>
                                    </div>
                                  )}
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: formatPriceWithLocale(r.price, schema.price_mask, languageTag, true, true)
                                    }}
                                  />
                                </div>
                                <CartPriceColumn
                                  price={response.answers[ri].price}
                                  priceMask={schema.price_mask}
                                  answer={answer}
                                  subscribe={response.answers[ri].subscribe}
                                />
                              </>
                            )}
                            <div className={style.row}>
                              <SpinButton
                                styles={{ width: '105px' }}
                                value={r.v}
                                index={r.id}
                                limit={schema.max_qty_per_product}
                                onClick={(qnt: 1 | -1, id: string) =>
                                  adjustQuantity(qnt, id, response.answers[ri].subscribe)
                                }
                                onSpin={(event: React.KeyboardEvent, id: string) =>
                                  onKeyDownAdjustQuantity(event, id, response.answers[ri].subscribe)
                                }
                              />
                              <span className={style.askPipe}>|</span>
                              <button
                                className={style.link}
                                onClick={() => {
                                  timeline.log({
                                    type: 'delete_from_cart',
                                    ref: response.answers[ri].id,
                                    ...(response.answers[ri].subscribe
                                      ? { context: { weeks: response.answers[ri].subscribe } }
                                      : {})
                                  })
                                  response.answers.splice(ri, 1)
                                  onUpdateResponse(response)
                                }}
                              >
                                {t('questions', 'ec', 'amazon', 'delete')}
                              </button>

                              <span className={style.askPipe}>|</span>
                              <EmptyLink>
                                <span className={style.link}>{t('questions', 'ec', 'amazon', 'save')}</span>
                              </EmptyLink>
                              <span className={style.askPipe}>|</span>
                              <EmptyLink>
                                <span className={style.link}>{t('questions', 'ec', 'amazon', 'compare')}</span>
                              </EmptyLink>
                              <span className={style.askPipe}>|</span>
                              <EmptyLink>
                                <span className={style.link}>{t('questions', 'ec', 'amazon', 'share')}</span>
                              </EmptyLink>
                            </div>
                          </div>
                          {!viewport.mobile && (
                            <div className={style.priceColumn}>
                              <CartPriceColumn
                                price={response.answers[ri].price}
                                priceMask={schema.price_mask}
                                answer={answer}
                                subscribe={response.answers[ri].subscribe}
                              />
                            </div>
                          )}
                        </div>
                        {viewport.mobile && (
                          <div className={style.quantityBlock}>
                            <SpinButton
                              styles={{ width: '125px' }}
                              value={r.v}
                              index={r.id}
                              limit={schema.max_qty_per_product}
                              onClick={(qnt: 1 | -1, id: string) =>
                                adjustQuantity(qnt, id, response.answers[ri].subscribe)
                              }
                              onSpin={(event: React.KeyboardEvent, id: string) =>
                                onKeyDownAdjustQuantity(event, id, response.answers[ri].subscribe)
                              }
                            />
                            <button
                              className={style.quantityBlockAction}
                              onClick={() => {
                                timeline.log({
                                  type: 'delete_from_cart',
                                  ref: response.answers[ri].id,
                                  ...(response.answers[ri].subscribe
                                    ? { context: { weeks: response.answers[ri].subscribe } }
                                    : {})
                                })
                                response.answers[ri].v = 0
                                onUpdateResponse(response)
                              }}
                            >
                              {t('questions', 'ec', 'amazon', 'delete')}
                            </button>
                            <EmptyLink>
                              <button className={style.quantityBlockAction}>
                                {t('questions', 'ec', 'amazon', 'save')}
                              </button>
                            </EmptyLink>
                            <EmptyLink>
                              <button className={style.quantityBlockAction}>
                                {t('questions', 'ec', 'amazon', 'compare')}
                              </button>
                            </EmptyLink>
                          </div>
                        )}
                        <div className={style.divider} />
                      </div>
                    ) : (
                      <div key={ri} />
                    )}
                  </div>
                )
              })}
            </div>
            <div className={`${style.subTotal} ${style.mobileHidden}`}>
              {t<(total: number) => string>('questions', 'ec', 'amazon', 'subtotal')(totals[0])}:{' '}
              <span
                dangerouslySetInnerHTML={{
                  __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true)
                }}
              />
            </div>
          </div>
          {viewport.mobile && (
            <div className={style.noItems}>
              <button onClick={closeCart}>{t('questions', 'ec', 'amazon', 'continueShopping')}</button>
            </div>
          )}
          <div className={style.info}>
            <span
              dangerouslySetInnerHTML={{
                __html: t<(store: string) => string>('questions', 'ec', 'amazon', 'info1')(STORE)
              }}
            />
            <EmptyLink>
              <span className={style.link}>&nbsp;{t('questions', 'ec', 'amazon', 'learnMore')}</span>
            </EmptyLink>
            <br />
            <span dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'amazon', 'info2') }} />
          </div>
        </div>
        <div className={`${style.rightColumn}`}>
          {cart.length ? (
            <div className={style.checkoutBlock}>
              {viewport.mobile ? (
                <>
                  <div className={style.subTotal}>
                    {t<(total: number, subtotal: boolean) => string>(
                      'questions',
                      'ec',
                      'amazon',
                      'subtotal'
                    )(totals[0], true)}
                    :{' '}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true)
                      }}
                    />
                  </div>
                  {renderGreenBox()}
                  {renderError()}
                  <button className={`${style.add} ${!valid ? style.disabled : ''}`} onClick={checkout}>
                    {t<(total: number) => string>('questions', 'ec', 'amazon', 'checkoutMobile')(totals[0])}
                  </button>
                </>
              ) : (
                <>
                  {renderGreenBox()}
                  {renderError()}
                  <div className={style.subTotal}>
                    {t<(total: number, subtotal: boolean) => string>(
                      'questions',
                      'ec',
                      'amazon',
                      'subtotal'
                    )(totals[0], true)}{' '}
                    <span
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true)
                      }}
                    />
                  </div>
                  <button className={`${style.add} ${!valid ? style.disabled : ''}`} onClick={checkout}>
                    {t('questions', 'ec', 'amazon', 'checkout')}
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {na && (
                <button className={`${style.na}`} onClick={onNaSelect}>
                  {na.text || t('questions', 'ec', 'na')}
                </button>
              )}
            </>
          )}
          {/* <div className={style.prime}>
            <img
              src="https://m.media-amazon.com/images/G/01/marketing/prime/new_prime_logo_RGB_blue._CB426090081_.png"
              alt="Amazon prime logo"
              className="prime-bbop-logo"
            />
            <span className={style.base}>
              <b>Enjoy fast, free delivery, exclusive deals, and award-winning movies & TV shows with Prime</b>
            </span>
            <span className={style.base}>
              Try Prime and start saving today with&nbsp;
              <b>fast, free delivery</b>
            </span>
          </div> */}
        </div>
      </div>
    </div>
  )
}

export default Cart
